import React, { useRef, useState, useEffect } from "react"
import { Button, Input, Card } from '../components/primitives/';
import { useAuth } from "../contexts/AuthContext"
import { Link, useParams, Navigate, useNavigate } from "react-router-dom"
import Logo from '../assets/pplogo.png';
import { useTranslation } from '../hooks/useTranslation';
import useTimedError from "../hooks/useTimedError";
import serviceIcons from '../assets/service-icons';

export default function ResetPassword() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const { getPasswordResetRequestEmail, resetPassword, login } = useAuth();
    const [userEmail, setUserEmail] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useTimedError();
    const [message, setMessage] = useState("");
    const [passwordComplexity, setPasswordComplexity] = useState([
        {
            name: "length",
            value: false,
            message: t("At Least 8 Characters"),
            error: t("Password must be at least 8 characters long")
        },
        {
            name: "uppercase",
            value: false,
            message: t("At Least One Uppercase Letter"),
            error: t("Password must contain at least one uppercase letter")
        },
        {
            name: "lowercase",
            value: false,
            message: t("At Least One Lowercase Letter"),
            error: t("Password must contain at least one lowercase letter")
        },
        {
            name: "number",
            value: false,
            message: t("At Least One Number"),
            error: t("Password must contain at least one number")
        },
        {
            name: "matching",
            value: false,
            message: t("Matching Passwords"),
            error: t("Passwords do not match")
        }
    ]);
    const [passwordComplexityVisible, setPasswordComplexityVisible] = useState(false);
    
    const [pass, setPass] = useState("");
    const [passConfirm, setPassConfirm] = useState("");

    const handlePassChange = (event) => {
        setPass(event.target.value);
        setPasswordComplexity(passwordComplexity.map((complexity) => ({
            ...complexity,
            value: (() => {
                switch (complexity.name) {
                    case "length":
                        return event.target.value.length >= 8;
                    case "uppercase":
                        return /[A-Z]/.test(event.target.value);
                    case "lowercase":
                        return /[a-z]/.test(event.target.value);
                    case "number":
                        return /\d/.test(event.target.value);
                    case "matching":
                        return event.target.value && (event.target.value === passConfirm);
                    default:
                        return false;
                }
            })()
        })));
    };

    const handlePassConfirmChange = (event) => {
        setPassConfirm(event.target.value);
        setPasswordComplexity(passwordComplexity.map((complexity) => ({
            ...complexity,
            value: complexity.name === "matching" ? event.target.value && (event.target.value === pass) : complexity.value
        })));
    };

    useEffect(() => {
        (async () => {
            if (!id) {
                navigate("/", { replace: true });
                return;
            }
            try {
                const email = await getPasswordResetRequestEmail(id);
                if (email) {
                    setUserEmail(email);
                } else {
                    navigate("/", { replace: true });
                }
            } catch (err) {
                setError(t("Invalid Request"));
            }
            setLoading(false);
        })();
    }, [id, getPasswordResetRequestEmail]);

    async function handleSubmit(e) {
        e.preventDefault();

        setLoading(true);

        try {
            passwordComplexity.forEach((complexity) => {
                if (!complexity.value) {
                    throw new Error(complexity.error);
                }
            });
        } catch (error) {
            setError(error.message);
            return;
        }

        try {
            await resetPassword(id, pass);
            setMessage(t("Password reset successful. Redirecting to dashboard..."));
            await login(userEmail, pass);
            setTimeout(async () => {
                navigate("/auth/dashboard");
            }, 3000);
        } catch (error) {
            setError(t("Failed to reset password"));
            console.error("Error resetting password:", error);
            setLoading(false);
        }
    }

    return (
        <div className="auth-page">
        <div className="auth-outer-container">
            <div className="logo-group">
                <div className="logo-group-inner">
                    <img className="logo-signin" src={Logo} alt="Logo" />
                    <h2 className="logo-heading w-900 text-center">{t('Page Parrot')}</h2>
                </div>
            </div>
            <Card className="auth-container centered">
            <h2 className="text-center mb-4">{t('Reset Password')}</h2>
            <div className="auth-info-text">
                {t('Please enter your new password below.')}
                <br/>
                {message && <span className="success">{message}</span>}
            </div>
            <form onSubmit={handleSubmit}>
                <Input
                    value={userEmail} 
                    labelText={t('Email')} 
                    id="email" 
                    disabled={true} 
                />
                <Input
                    type="password" 
                    labelText={t('New Password')} 
                    id="password1" 
                    required={true}
                    value={pass}
                    onChange={handlePassChange}
                    onFocus={() => setPasswordComplexityVisible(true)}
                />
                <Input
                    type="password" 
                    labelText={t('Confirm New Password')} 
                    id="password2" 
                    required={true}
                    value={passConfirm}
                    onChange={handlePassConfirmChange}
                    onFocus={() => setPasswordComplexityVisible(true)}
                />
                {passwordComplexityVisible && <div className="password-complexity mv-15">
                    {passwordComplexity.map((complexity) => (
                        <div key={complexity.name} className={`password-complexity-item ${complexity.value ? "valid" : "invalid"} entering`}>
                            {complexity.value ? <img src={serviceIcons["CheckMark.svg"]} alt="Check Mark" className="password-complexity-icon valid" /> : <img src={serviceIcons["XMark.svg"]} alt="X Mark" className="password-complexity-icon invalid" />}
                            <span className="password-complexity-message">{complexity.message}</span>
                        </div>
                    ))}
                </div>}
                <Button disabled={loading} buttonType="submit" error={error} loading={loading}>
                    {error ? error : t('Reset Password')}
                </Button>
            </form>
            <div className="w-500 fs-12 text-center mt-3">
                <Link to="/login">{t('Back to Sign In')}</Link>
            </div>
            </Card>
        </div>
        </div>
    )
}
